import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiClient, api, getCurrentTenantUserId } from '../../api/client';
import './NewConversationModal.css';
import { translateError } from '../../utils/errorMessages';

interface User {
  _id: string;
  name: string;
  email: string;
}

interface Member {
  _id: string;
  name: string;
  user?: User;
}

interface Group {
  _id: string;
  name: string;
  description?: string;
}

interface Conversation {
  _id: string;
  type: 'direct' | 'group';
  participants: { _id: string; name: string; email: string }[];
  group?: { _id: string; name: string };
  name?: string;
  lastMessage: {
    content: string;
    sender: string;
    timestamp: string;
  } | null;
  updatedAt: string;
}

interface NewConversationModalProps {
  onClose: () => void;
  onConversationCreated: (conversation: Conversation) => void;
}

type TabType = 'users' | 'members' | 'groups';

const NewConversationModal: React.FC<NewConversationModalProps> = ({ onClose, onConversationCreated }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Tenant user id — the users list returns tenant users.id, so self-exclusion
  // must compare against the tenant id (not the global account id).
  const currentTenantUserId = getCurrentTenantUserId();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [usersRes, membersRes, groupsRes] = await Promise.all([
          api.users.list(),
          api.members.list(),
          api.groups.list(),
        ]);
        // Filter out current user
        setUsers((usersRes.data as User[]).filter(u => String(u._id) !== currentTenantUserId));
        // Filter to only members with caregivers
        setMembers((membersRes.data as Member[]).filter(m => m.user));
        setGroups(groupsRes.data as Group[]);
      } catch (err: any) {
        setError(translateError(err, t));
      }
      setLoading(false);
    };
    fetchData();
  }, [currentTenantUserId]);

  const handleSelectUser = async (user: User) => {
    if (creating) return;
    setCreating(true);
    setError(null);

    try {
      const { data } = await apiClient.post<Conversation>(
        '/api/messages/conversations',
        { recipientId: user._id }
      );
      onConversationCreated(data);
    } catch (err: any) {
      setError(translateError(err, t));
      setCreating(false);
    }
  };

  const handleSelectMember = async (member: Member) => {
    if (!member.user || creating) return;
    setCreating(true);
    setError(null);

    try {
      const { data } = await apiClient.post<Conversation>(
        '/api/messages/conversations',
        { memberId: member._id }
      );
      onConversationCreated(data);
    } catch (err: any) {
      setError(translateError(err, t));
      setCreating(false);
    }
  };

  const handleSelectGroup = async (group: Group) => {
    if (creating) return;
    setCreating(true);
    setError(null);

    try {
      const { data } = await apiClient.post<Conversation>(
        '/api/messages/conversations',
        { groupId: group._id }
      );
      onConversationCreated(data);
    } catch (err: any) {
      setError(translateError(err, t));
      setCreating(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.user?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getSearchPlaceholder = () => {
    switch (activeTab) {
      case 'users':
        return t('messaging.searchUsers');
      case 'members':
        return t('messaging.searchMembers');
      case 'groups':
        return t('messaging.searchGroups');
    }
  };

  const getEmptyMessage = () => {
    if (searchTerm) return t('messaging.noResults');
    switch (activeTab) {
      case 'users':
        return t('messaging.noUsers');
      case 'members':
        return t('messaging.noMembersWithCaregivers');
      case 'groups':
        return t('messaging.noGroups');
    }
  };

  return createPortal(
    <div className="new-conversation-modal" onClick={handleBackdropClick}>
      <div className="new-conversation-modal__content">
        <div className="new-conversation-modal__header">
          <h3>{t('messaging.newConversation')}</h3>
          <button className="new-conversation-modal__close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="new-conversation-modal__tabs">
          <button
            className={`new-conversation-modal__tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            {t('messaging.tabUsers')}
          </button>
          <button
            className={`new-conversation-modal__tab ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            {t('messaging.tabMembers')}
          </button>
          <button
            className={`new-conversation-modal__tab ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            {t('messaging.tabGroups')}
          </button>
        </div>

        <div className="new-conversation-modal__search">
          <input
            type="text"
            id="new-conversation-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={getSearchPlaceholder()}
          />
        </div>

        {error && (
          <div className="new-conversation-modal__error">
            {error}
          </div>
        )}

        <div className="new-conversation-modal__list">
          {loading ? (
            <div className="new-conversation-modal__loading">
              <div className="new-conversation-modal__spinner"></div>
            </div>
          ) : (
            <>
              {/* Users Tab */}
              {activeTab === 'users' && (
                filteredUsers.length === 0 ? (
                  <div className="new-conversation-modal__empty">
                    {getEmptyMessage()}
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <button
                      key={user._id}
                      className="new-conversation-modal__item"
                      onClick={() => handleSelectUser(user)}
                      disabled={creating}
                    >
                      <div className="new-conversation-modal__avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="new-conversation-modal__info">
                        <span className="new-conversation-modal__name">{user.name}</span>
                        <span className="new-conversation-modal__subtitle">{user.email}</span>
                      </div>
                    </button>
                  ))
                )
              )}

              {/* Members Tab */}
              {activeTab === 'members' && (
                filteredMembers.length === 0 ? (
                  <div className="new-conversation-modal__empty">
                    {getEmptyMessage()}
                  </div>
                ) : (
                  filteredMembers.map(member => (
                    <button
                      key={member._id}
                      className="new-conversation-modal__item"
                      onClick={() => handleSelectMember(member)}
                      disabled={creating}
                    >
                      <div className="new-conversation-modal__avatar">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="new-conversation-modal__info">
                        <span className="new-conversation-modal__name">{member.name}</span>
                        <span className="new-conversation-modal__subtitle">
                          {t('messaging.caregiver')}: {member.user?.name}
                        </span>
                      </div>
                    </button>
                  ))
                )
              )}

              {/* Groups Tab */}
              {activeTab === 'groups' && (
                filteredGroups.length === 0 ? (
                  <div className="new-conversation-modal__empty">
                    {getEmptyMessage()}
                  </div>
                ) : (
                  filteredGroups.map(group => (
                    <button
                      key={group._id}
                      className="new-conversation-modal__item"
                      onClick={() => handleSelectGroup(group)}
                      disabled={creating}
                    >
                      <div className="new-conversation-modal__avatar new-conversation-modal__avatar--group">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                          <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" clipRule="evenodd" />
                          <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
                        </svg>
                      </div>
                      <div className="new-conversation-modal__info">
                        <span className="new-conversation-modal__name">{group.name}</span>
                        {group.description && (
                          <span className="new-conversation-modal__subtitle">{group.description}</span>
                        )}
                      </div>
                    </button>
                  ))
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewConversationModal;
